/**
 * SnappiPit web notification sound.
 *
 * Browsers can block audible playback until the user has interacted with the
 * page. initNotificationSound() therefore primes the shared audio element on
 * the first pointer/key interaction. playNotificationSound() is best-effort
 * and deduplicates the same notification arriving through FCM + WebSocket.
 */
// This asset is present in both the source public/ tree and the distributed
// build/ tree. The former root-level copy is absent from existing build ZIPs.
const SOUND_PATH = '/sounds/snappipit_notification.mp3';
const DEDUPE_MS = 2500;

let audio = null;
let initialized = false;
const recentlyPlayed = new Map();

function getAudio() {
  if (typeof window === 'undefined' || typeof Audio === 'undefined') return null;
  if (!audio) {
    audio = new Audio(SOUND_PATH);
    audio.preload = 'auto';
    audio.volume = 1.0;
  }
  return audio;
}

function cleanup(now) {
  recentlyPlayed.forEach((at, key) => {
    if (now - at > DEDUPE_MS * 4) recentlyPlayed.delete(key);
  });
}

export function initNotificationSound() {
  if (initialized || typeof window === 'undefined') return;
  initialized = true;

  const unlock = () => {
    const player = getAudio();
    if (!player) return;
    const previousVolume = player.volume;
    player.volume = 0;
    let result;
    try {
      result = player.play();
    } catch {
      player.volume = previousVolume;
      return;
    }
    const finish = () => {
      player.pause();
      player.currentTime = 0;
      player.volume = previousVolume;
      window.removeEventListener('pointerdown', unlock, true);
      window.removeEventListener('keydown', unlock, true);
    };
    if (result && typeof result.then === 'function') {
      result
        .then(finish)
        .catch(() => {
          player.volume = previousVolume;
        });
    } else {
      finish();
    }
  };

  window.addEventListener('pointerdown', unlock, true);
  window.addEventListener('keydown', unlock, true);
  getAudio();
}

export function playNotificationSound(notificationKey) {
  const player = getAudio();
  if (!player) return;

  const now = Date.now();
  const key = String(notificationKey || `notification-${now}`);
  cleanup(now);
  const previous = recentlyPlayed.get(key);
  if (previous && now - previous < DEDUPE_MS) return;
  try {
    player.pause();
    player.currentTime = 0;
    const result = player.play();
    if (result && typeof result.then === 'function') {
      result.then(() => recentlyPlayed.set(key, Date.now())).catch(() => {});
    } else {
      recentlyPlayed.set(key, now);
    }
  } catch {
    // Browser autoplay policy / audio-device failure: notification UI still works.
  }
}
